import { ChevronDown, ChevronUp, ThumbsDown } from 'lucide-react';
import { useRef, useState } from 'react';

import {
  useGetTaskInstructionQuery,
  useSubmitTaskInstructionFeedbackMutation,
} from '@/services/api/tasksService';

interface TaskInstructionCardProps {
  matterId: string;
  taskId: string;
}

export const TaskInstructionCard = ({
  matterId,
  taskId,
}: TaskInstructionCardProps) => {
  const [showReasoning, setShowReasoning] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [showFeedbackPopover, setShowFeedbackPopover] = useState(false);
  const [feedbackReason, setFeedbackReason] = useState('');
  const popoverRef = useRef<HTMLDivElement>(null);

  const {
    data: instruction,
    isLoading,
    error,
  } = useGetTaskInstructionQuery(
    { matterId, taskId },
    { skip: !matterId || !taskId }
  );

  const [submitFeedback, { isLoading: isSubmitting }] =
    useSubmitTaskInstructionFeedbackMutation();

  if (isLoading) {
    return (
      <div className="rounded-md border border-slate-300 bg-slate-50 px-3 py-2.5">
        <div className="flex items-center gap-1.5 text-xs text-slate-400">
          <span>Loading instructions...</span>
        </div>
      </div>
    );
  }

  if (error || !instruction) {
    return null;
  }

  const handleThumbsDown = async () => {
    if (instruction.thumbs_down) return; // Already submitted
    setShowFeedbackPopover(true);
  };

  const handleSubmitFeedback = async () => {
    await submitFeedback({
      matterId,
      taskId,
      reason: feedbackReason || undefined,
    });
    setShowFeedbackPopover(false);
    setFeedbackReason('');
  };

  const handleSkipFeedback = async () => {
    await submitFeedback({ matterId, taskId });
    setShowFeedbackPopover(false);
    setFeedbackReason('');
  };

  return (
    <div className="rounded-md border-2 border-slate-800 bg-white mb-2">
      {/* Header — always visible, acts as collapse toggle */}
      <button
        type="button"
        onClick={() => setCollapsed(!collapsed)}
        className="flex w-full items-center justify-between px-3 pt-1.5 pb-1.5"
      >
        <p className="text-[11px] font-black uppercase tracking-tight text-slate-900">
          Instructions
        </p>
        <div className="flex items-center gap-1.5">
          {/* Thumbs-down button */}
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              handleThumbsDown();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.stopPropagation();
                handleThumbsDown();
              }
            }}
            className={`relative p-0.5 rounded transition-colors ${
              instruction.thumbs_down
                ? 'text-red-500'
                : 'text-slate-400 hover:text-slate-600'
            }`}
            title={
              instruction.thumbs_down
                ? 'Feedback submitted'
                : 'Mark as unhelpful'
            }
          >
            <ThumbsDown
              className="h-3 w-3"
              fill={instruction.thumbs_down ? 'currentColor' : 'none'}
            />
          </span>

          {/* Collapse toggle */}
          {collapsed ? (
            <ChevronDown className="h-3.5 w-3.5 text-slate-800" />
          ) : (
            <ChevronUp className="h-3.5 w-3.5 text-slate-800" />
          )}
        </div>
      </button>

      {/* Feedback popover */}
      {showFeedbackPopover && (
        <div
          ref={popoverRef}
          className="mx-3 mb-2 rounded-md border border-slate-200 bg-slate-50 p-2.5"
        >
          <p className="text-[11px] font-semibold text-slate-700 mb-1.5">
            What was wrong? (optional)
          </p>
          <textarea
            value={feedbackReason}
            onChange={(e) => setFeedbackReason(e.target.value)}
            placeholder="e.g. Wrong contact info, irrelevant suggestion..."
            className="w-full rounded border border-slate-300 px-2 py-1.5 text-[11px] text-slate-800 placeholder:text-slate-400 focus:border-slate-500 focus:outline-none resize-none"
            rows={2}
          />
          <div className="flex justify-end gap-1.5 mt-1.5">
            <button
              type="button"
              onClick={handleSkipFeedback}
              disabled={isSubmitting}
              className="rounded px-2 py-1 text-[10px] font-medium text-slate-500 hover:text-slate-700 transition-colors"
            >
              Skip
            </button>
            <button
              type="button"
              onClick={handleSubmitFeedback}
              disabled={isSubmitting}
              className="rounded bg-slate-800 px-2.5 py-1 text-[10px] font-medium text-white hover:bg-slate-700 transition-colors"
            >
              {isSubmitting ? 'Sending...' : 'Submit'}
            </button>
          </div>
        </div>
      )}

      {/* Collapsible content */}
      {!collapsed && (
        <>
          {/* Recap */}
          {instruction.recap && (
            <div className="px-3 pb-1.5">
              <p className="text-[12px] font-bold leading-snug text-slate-700">
                {instruction.recap}
              </p>
            </div>
          )}

          {/* Next Actions */}
          <div className="px-3 pb-2 mt-1">
            <ol className="space-y-2">
              {instruction.next_actions.map((action, i) => (
                <li key={i} className="flex gap-1.5 text-[11px]">
                  <span className="mt-px flex h-[14px] w-[14px] flex-shrink-0 items-center justify-center rounded-full bg-slate-900 text-[9px] font-bold text-white">
                    {i + 1}
                  </span>
                  <span className="font-bold text-xs text-slate-900">
                    {action}
                  </span>
                </li>
              ))}
            </ol>
          </div>

          {/* Reasoning (expandable) */}
          <div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowReasoning(!showReasoning);
              }}
              className="flex w-full items-center justify-between px-3 py-1.5 text-[11px] text-slate-600 hover:text-slate-800 transition-colors"
            >
              <span>Why?</span>
              {showReasoning ? (
                <ChevronUp className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
            </button>
            {showReasoning && (
              <div className="px-3 pb-2.5">
                <p className="text-[11px] leading-relaxed text-slate-800">
                  {instruction.reasoning}
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
